import React from 'react';

const CheckboxField = ({
  label,
  name,
  checked,
  onChange,
  disabled = false,
  error,
  helpText,
  description,
  className = '',
  size = 'default',
  color = 'blue',
  ...props
}) => {
  const handleChange = (e) => {
    const { name, checked } = e.target;
    onChange({ [name]: checked });
  };

  const fieldId = `checkbox-${name}`;
  const hasError = Boolean(error);

  const sizeClasses = {
    small: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  const colorClasses = {
    blue: 'text-blue-600 focus:ring-blue-500',
    green: 'text-green-600 focus:ring-green-500',
    red: 'text-red-600 focus:ring-red-500',
    amber: 'text-amber-600 focus:ring-amber-500',
    purple: 'text-purple-600 focus:ring-purple-500'
  };

  const checkboxClasses = `
    ${sizeClasses[size]} rounded border-gray-300 transition-colors duration-200
    ${colorClasses[color]}
    focus:ring-2 focus:ring-offset-2
    disabled:bg-gray-100 disabled:cursor-not-allowed
    ${hasError ? 'border-red-300 focus:ring-red-500' : ''}
  `;

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-start space-x-3">
        {/* Checkbox Input */}
        <div className="flex items-center h-5">
          <input
            id={fieldId}
            name={name}
            type="checkbox"
            checked={checked || false}
            onChange={handleChange}
            disabled={disabled}
            className={checkboxClasses.trim()}
            {...props}
          />
        </div>

        {/* Label and Description */}
        <div className="flex-1">
          {label && (
            <label 
              htmlFor={fieldId}
              className={`text-sm font-medium cursor-pointer ${
                hasError ? 'text-red-700' : 'text-gray-900'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {label}
            </label>
          )}

          {description && (
            <p className={`text-sm mt-1 ${
              hasError ? 'text-red-600' : 'text-gray-600'
            } ${disabled ? 'opacity-50' : ''}`}>
              {description}
            </p>
          )}

          {/* Help Text */}
          {helpText && !hasError && (
            <p className={`text-xs mt-1 text-gray-500 ${disabled ? 'opacity-50' : ''}`}>
              {helpText}
            </p>
          )}

          {/* Error Message */}
          {hasError && (
            <p className="text-sm text-red-600 flex items-center mt-1">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Checkbox Group Component for multiple related checkboxes
export const CheckboxGroup = ({
  label,
  name,
  options = [],
  values = [],
  onChange,
  disabled = false,
  error,
  helpText,
  className = '',
  columns = 1,
  ...props
}) => {
  const handleCheckboxChange = (optionValue, checked) => {
    let newValues;
    if (checked) {
      newValues = [...values, optionValue];
    } else {
      newValues = values.filter(v => v !== optionValue);
    }
    onChange({ [name]: newValues });
  };

  const hasError = Boolean(error);
  const gridClass = columns > 1 ? `grid grid-cols-${columns} gap-2` : 'space-y-2';

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Group Label */}
      {label && (
        <label className={`block text-sm font-medium ${hasError ? 'text-red-700' : 'text-gray-700'}`}>
          {label}
        </label>
      )}

      {/* Checkbox Options */}
      <div className={gridClass}>
        {options.map((option, index) => {
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          const isChecked = values.includes(optionValue);
          
          return (
            <CheckboxField
              key={optionValue || index}
              name={`${name}-${optionValue}`}
              label={optionLabel}
              checked={isChecked}
              onChange={() => handleCheckboxChange(optionValue, !isChecked)}
              disabled={disabled}
              {...props}
            />
          );
        })}
      </div>

      {/* Help Text */}
      {helpText && !hasError && (
        <p className="text-sm text-gray-500">{helpText}</p>
      )}

      {/* Error Message */}
      {hasError && (
        <p className="text-sm text-red-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

export default CheckboxField;
